import { Injectable } from '@angular/core';

import { UserInfo } from '../models';
import { RequestStatus, RequestType, RequestTypeForValidation } from '@app/shared/types';
import { UserInfoService } from './state';
import { take } from 'rxjs';
import { NeedVerifyItems } from '@app/shared/models';

@Injectable({
    providedIn: 'root'
})

export class CheckVerifyService {

    private userInfo: UserInfo;

    mobileNumberIsVerified: boolean;
    personalIsVerified: boolean;
    addressVerified: boolean;
    photoVerified: boolean;
    phoneCallInterviewVerified: boolean;
    needVerify: boolean;

    constructor(
        private userInfoService: UserInfoService
    ) {
        this.fetchUserInfo();
    }


    private padLeft(num: string): string {
        switch (num.length) {
            case 1:
                return '000' + num;
            case 2:
                return '00' + num;
            case 3:
                return '0' + num;
            default:
                return num;

        }
    }

    private reverseString(str: string): string {
        return str.split('').reverse().join('');
    }

    isRequestCompleted(requestStatus: RequestStatus): boolean {
        if (
            requestStatus === RequestStatus.DONE ||
            requestStatus === RequestStatus.FAILED ||
            requestStatus === RequestStatus.REFUND ||
            requestStatus === RequestStatus.REJECTED ||
            requestStatus === RequestStatus.DELETED
        ) {
            return true;
        }
        return false;
    }

    checkValidation(validationRequirement: number): boolean {
        const number = validationRequirement;
        const binValue = this.padLeft(number.toString(2));
        const revValue = this.reverseString(binValue);
        if (revValue.charAt(0) === '1' && !this.personalIsVerified) {
            return true;
        }
        if (revValue.charAt(1) === '1' && !this.photoVerified) {
            return true;
        }
        if (revValue.charAt(2) === '1' && !this.addressVerified) {
            return true;
        }
        if (revValue.charAt(3) === '1' && !this.mobileNumberIsVerified) {
            return true;
        }
        if (revValue.charAt(4) === '1' && !this.phoneCallInterviewVerified && !this.userInfo.information.trusted) {
            return true;
        }
        return false;
    }

    checkValidationWithRequestType(requestType: RequestTypeForValidation): boolean {
        const number = this.getRequestNumber(requestType);
        const binValue = this.padLeft(number.toString(2));
        const revValue = this.reverseString(binValue);
        if (revValue.charAt(0) === '1' && !this.personalIsVerified) {
            return true;
        }
        if (revValue.charAt(1) === '1' && !this.photoVerified) {
            return true;
        }
        if (revValue.charAt(2) === '1' && !this.addressVerified) {
            localStorage.setItem('needVerifyAddress', 'true');
            return true;
        }
        if (revValue.charAt(3) === '1' && !this.mobileNumberIsVerified) {
            return true;
        }
        if (revValue.charAt(4) === '1' && !this.phoneCallInterviewVerified && !this.userInfo.information.trusted) {
            return true;
        }
        return false;
    }


    getRequestNumber(requestType: RequestTypeForValidation): number {
        switch (requestType) {
            case RequestTypeForValidation.DepositMoneyCurrency:
                return this.userInfo.requestValidation.depositLocalPayment;

            case RequestTypeForValidation.DepositElectronicMoney:
                return this.userInfo.requestValidation.depositEMoney;

            case RequestTypeForValidation.ExchangeMoneyCurrency:
                return this.userInfo.requestValidation.exchangeTcWallet;

            case RequestTypeForValidation.WithdrawalElectronicMoney:
                return this.userInfo.requestValidation.withdrawalEMoney;

            case RequestTypeForValidation.WithdrawalMoneyCurrency:
                return this.userInfo.requestValidation.withdrawalLocalPayment;

            case RequestTypeForValidation.Voucher:
                return this.userInfo.requestValidation.voucher;

            case RequestTypeForValidation.SendTcWallet:
                return this.userInfo.requestValidation.sendTcWallet;

            case RequestTypeForValidation.SendOtherWay:
                return this.userInfo.requestValidation.sendLocalPayment;

            case RequestTypeForValidation.Point:
                return this.userInfo.requestValidation.pointWithdrawal;

        }
    }

    reload(): void {
        this.fetchUserInfo();
    }

    private fetchUserInfo(): void {
        this.userInfoService.getUserInfo$()
            .subscribe((userInfo: UserInfo) => {
                this.userInfo = userInfo;
                this.setVerificationState();
            });
    }

    private setVerificationState(): void {
        if (this.userInfo && this.userInfo.information) {
            this.mobileNumberIsVerified = this.userInfo.information.mobileVerified;
            this.personalIsVerified = this.userInfo.information.identityVerified;
            this.addressVerified = this.userInfo.information.addressVerified;
            this.photoVerified = this.userInfo.information.photoVerified;
            this.phoneCallInterviewVerified = this.userInfo.information.interviewVerified;
        } else {
            this.mobileNumberIsVerified = false;
            this.personalIsVerified = false;
            this.addressVerified = false;
            this.photoVerified = false;
        }
    }

    getNeedVerifyItems(validationRequirement: number): NeedVerifyItems {
        const needVerify: NeedVerifyItems = {
            needAddressVerify: false,
            needMobileVerify: false,
            needPersonalVerify: false,
            needPhotoVerify: false,
            needPhoneCallInterviewVerify: false
        };

        const number = validationRequirement;
        const binValue = this.padLeft(number.toString(2));
        const revValue = this.reverseString(binValue);
        if (revValue.charAt(0) === '1') {
            needVerify.needPersonalVerify = true;
        }
        if (revValue.charAt(1) === '1') {
            needVerify.needPhotoVerify = true;
        }
        if (revValue.charAt(2) === '1') {
            needVerify.needAddressVerify = true;
        }
        if (revValue.charAt(3) === '1') {
            needVerify.needMobileVerify = true;
        }
        if (revValue.charAt(4) === '1') {
            needVerify.needPhoneCallInterviewVerify = true;
        }
        return needVerify;
    }

    /*******************************************************************
     Styles
    *******************************************************************/

    manageStepperStyle(width: number, tabsCount: number): string {
        if (width < 510) {
            return 'stepper-xs';

        } else if (width > 509 && width < 728) {
            return 'stepper-sm';

        } else if (width > 727 && width < 1026) {
            return ('stepper-md stepper-md-' + tabsCount);

        } else if (width > 1026 && width < 1500) {
            return 'stepper-lg-' + tabsCount;

        } else if (width > 1499) {
            return 'stepper-xl-' + tabsCount;

        }
    }

}
